import { z } from "zod";

import { ModelConfigBaseShape } from "./model-config.schema";
import { RetiredEffortLevelFieldsShape } from "./retired-effort-level-fields";

/**
 * AiTaskModelOverrideSchema — the model an AI task runs on FOR ONE CALL.
 *
 * An AI task binds a prompt to a model. That binding is right for the prompt and
 * wrong for the call: the same assessor is invoked thousands of times a day in a
 * scheduled sweep, where cheap and good-enough wins, and a handful of times from
 * a button a human presses, where the frontier model is worth 15x. Without a way
 * to say that at the call, the only way to express it is a second task — which
 * means a second copy of the prompt, and the prompt is the expensive artifact.
 *
 * 🚨 THE FORK-BY-MODEL WORKAROUND ALREADY DRIFTS ON THE FIRST COPY, AND SILENTLY.
 * Measured in production on the engagement that filed NEX-2962: two AI tasks with
 * a byte-identical 94,268-char prompt (same md5) and identical input/output
 * schemas, intended to differ by model alone — and differing also in
 * `temperature`, 0.2 on the original against 0.7 on the "model-only" fork,
 * because `CreateTaskBodySchema` defaults an unsent temperature to 0.7. Nobody
 * asked for that and nothing reported it. It is the whole argument for this
 * schema, and it is why `temperature` is deliberately ABSENT below.
 *
 * ## What an override replaces, and what it leaves alone
 *
 * It names the ROUTING and the tuning that belongs to the routing. Everything
 * else about the task — prompt, schemas, few-shots, collections, temperature —
 * is untouched, because the point is to run THE SAME reasoning somewhere else.
 * Nothing is persisted either: the task keeps its own binding, and every other
 * caller of it is unaffected.
 *
 * - `modelName` + `modelProvider` are BOTH required. Half a pair is how a task
 *   ends up addressing an Anthropic endpoint with an OpenAI model id;
 *   `AITaskExecutor.parseTaskModelConfig` carries the account of that failure.
 * - `customModelId` is NOT inherited from the task. A BYOM endpoint replaces the
 *   routing outright — the executor runs `customModel.modelName` and ignores the
 *   platform pair — so carrying a stored one into an override that names a
 *   platform model would silently VOID the override. Name one here to override
 *   onto a custom endpoint instead.
 * - `temperature` is NOT here and is ALWAYS inherited from the task. It is
 *   provider-agnostic and part of the reasoning, not of the routing: a sweep on
 *   Haiku at the task's 0.2 is the same task, and one at the default 0.7 is not.
 * - The provider-specific knobs below are NOT inherited — not even when the
 *   override keeps the same provider. An override that names none runs with
 *   none, so name the one you want here. That is `mergeModelConfig`'s existing
 *   rule for a model change, shared verbatim with `PATCH /skills/tasks/:id` and
 *   `POST /skills/tasks/:id/duplicate` rather than restated: a level is specific
 *   to a model's dialect (legacy `extended` on Anthropic 4.6, adaptive `low`…`max`
 *   on 4.7+), so carrying one across a model change is not the safe default it
 *   looks like — and one rule that all three surfaces share cannot drift, which is
 *   the failure this schema exists to close.
 * - The four retired level names are REFUSED, not stripped — see
 *   `RetiredEffortLevelFieldsShape`.
 *
 * The merge itself is `mergeModelConfig`, applied by
 * `AITaskExecutor.parseTaskModelConfig`, which is where the rule is tested.
 */
export const AiTaskModelOverrideSchema = z.object({
  // `.min(1)` here where `ModelConfigBaseShape` has none: a stored blank name
  // has a documented fallback (route to the platform default pair), but a
  // blank one sent on a CALL is a caller mistake with no sensible reading.
  modelName: ModelConfigBaseShape.modelName.min(1, "modelName is required"),
  modelProvider: ModelConfigBaseShape.modelProvider,
  customModelId: z
    .string()
    .uuid()
    .optional()
    .describe("Run this call on a custom (BYOM) endpoint — the id from GET /custom-models"),
  reasoningLevel: ModelConfigBaseShape.reasoningLevel,
  thinkingDisplay: ModelConfigBaseShape.thinkingDisplay,
  ...RetiredEffortLevelFieldsShape
});

export type AiTaskModelOverride = z.infer<typeof AiTaskModelOverrideSchema>;
